import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { cambiarPassword } from '../../db/engine';
import { LogoMarca, NombreMarca } from '../Marca';
import {
  LayoutDashboard, Users, FileText, CreditCard, DollarSign,
  ClipboardList, AlertTriangle, UserCheck, FolderOpen, Settings,
  ChevronLeft, ChevronRight, LogOut, Shield, ClipboardCheck, Banknote, Calculator,
  KeyRound, X, Eye, EyeOff, BarChart3, ArrowUpDown, ChevronUp, ChevronDown, HelpCircle,
} from 'lucide-react';

// Orden por defecto del menú (cada usuario puede personalizarlo).
// Contabilidad no la pidió Ricardo en su orden; es solo para master, va antes de Administración.
const NAV_ITEMS = [
  { key: 'dashboard',   label: 'Dashboard',      icon: LayoutDashboard, path: '/' },
  { key: 'crm',         label: 'CRM Gestión',     icon: ClipboardList,   path: '/crm' },
  { key: 'clientes',    label: 'Clientes',        icon: Users,           path: '/clientes' },
  { key: 'polizas',     label: 'Pólizas',         icon: FileText,        path: '/polizas' },
  { key: 'solicitudes', label: 'Solicitudes',     icon: ClipboardCheck,  path: '/solicitudes' },
  { key: 'cobros',      label: 'Cobros',          icon: Banknote,        path: '/cobros' },
  { key: 'comisiones',  label: 'Comisiones',      icon: DollarSign,      path: '/comisiones' },
  { key: 'reclamos',    label: 'Reclamos',        icon: AlertTriangle,   path: '/reclamos' },
  { key: 'archivos',    label: 'Archivos',        icon: FolderOpen,      path: '/archivos' },
  { key: 'agentes',     label: 'Agentes',         icon: UserCheck,       path: '/agentes' },
  { key: 'reportes',    label: 'Reportes',        icon: BarChart3,       path: '/reportes' },
  { key: 'contabilidad',label: 'Contabilidad',    icon: Calculator,      path: '/contabilidad' },
  { key: 'admin',       label: 'Administración',  icon: Settings,        path: '/admin' },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout, canView, guardarOrdenModulos, iniciarTour } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  // Reordenar módulos (personalizado por usuario)
  const [showOrden, setShowOrden] = useState(false);
  const [ordenList, setOrdenList] = useState([]);
  const [savingOrden, setSavingOrden] = useState(false);

  const handleNav = (path) => {
    navigate(path);
  };

  const handleLogout = async () => {
    await logout();   // cierra la sesión en Supabase, no solo en la pantalla
    navigate('/login');
  };

  // Cambio de contraseña self-service
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');

  // Menú del usuario (pie del sidebar): compacto por defecto (solo usuario+rol);
  // al tocarlo despliega las opciones HACIA ARRIBA, para no tapar los módulos.
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const footerRef = useRef(null);
  const userMenuRef = useRef(null);
  const toggleUserMenu = () => {
    if (showUserMenu) { setShowUserMenu(false); return; }
    const r = footerRef.current?.getBoundingClientRect();
    if (r) setMenuRect(r);
    setShowUserMenu(true);
  };
  useEffect(() => {
    if (!showUserMenu) return;
    const onDown = (e) => {
      if (userMenuRef.current?.contains(e.target) || footerRef.current?.contains(e.target)) return;
      setShowUserMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showUserMenu]);
  const menuItemStyle = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--font)', fontWeight: 600, textAlign: 'left' };
  const hoverIn = e => { e.currentTarget.style.background = 'rgba(var(--sky-rgb),0.12)'; };
  const hoverOut = e => { e.currentTarget.style.background = 'transparent'; };

  const abrirCambioPwd = () => { setCurPwd(''); setNewPwd(''); setNewPwd2(''); setShowPwd(false); setPwdMsg(''); setShowPwdModal(true); };
  const cambiarPwd = async () => {
    setPwdMsg('');
    if (!curPwd || !newPwd) return setPwdMsg('Completa la contraseña actual y la nueva.');
    if (newPwd.length < 4) return setPwdMsg('La nueva contraseña es muy corta.');
    if (newPwd !== newPwd2) return setPwdMsg('La confirmación no coincide.');
    setSavingPwd(true);
    try {
      // La contraseña la maneja Supabase Auth: verifica la actual y cifra la nueva.
      const r = await cambiarPassword(user.email, curPwd, newPwd);
      if (!r.ok) { setPwdMsg(r.error); setSavingPwd(false); return; }
      setPwdMsg('✓ Contraseña actualizada.');
      setTimeout(() => setShowPwdModal(false), 1200);
    } catch (e) { setPwdMsg('Error: ' + (e.message || e)); }
    finally { setSavingPwd(false); }
  };

  // Aplica el orden personalizado del usuario; los módulos nuevos (no guardados)
  // se agregan al final en el orden por defecto.
  const orderedItems = (() => {
    const byKey = Object.fromEntries(NAV_ITEMS.map(i => [i.key, i]));
    let saved = [];
    try { saved = user?.orden_modulos ? JSON.parse(user.orden_modulos) : []; } catch { saved = []; }
    const seen = new Set();
    const result = [];
    for (const k of saved) { if (byKey[k] && !seen.has(k)) { result.push(byKey[k]); seen.add(k); } }
    for (const i of NAV_ITEMS) { if (!seen.has(i.key)) { result.push(i); seen.add(i.key); } }
    return result;
  })();
  const visibleItems = orderedItems.filter(item => canView(item.key));

  const abrirOrden = () => { setOrdenList(visibleItems.map(i => ({ key: i.key, label: i.label }))); setShowOrden(true); };
  const moverModulo = (idx, dir) => {
    setOrdenList(prev => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };
  const guardarOrden = async () => {
    setSavingOrden(true);
    try {
      await guardarOrdenModulos(ordenList.map(i => i.key));
      setShowOrden(false);
    } catch (e) { alert('Error al guardar el orden: ' + (e.message || e)); }
    finally { setSavingOrden(false); }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, bottom: 0,
      width: collapsed ? 72 : 260,
      background: 'linear-gradient(180deg, var(--navy-grad) 0%, var(--navy) 100%)',
      borderRight: '1px solid rgba(var(--silver-rgb),0.15)',
      display: 'flex', flexDirection: 'column',
      transition: 'width 0.3s ease',
      zIndex: 100, overflow: 'hidden'
    }}>
      {/* Logo */}
      <div style={{
        padding: collapsed ? '20px 0' : '20px 20px',
        borderBottom: '1px solid rgba(var(--silver-rgb),0.15)',
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight: 72
      }}>
        {!collapsed && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LogoMarca size={28} />
              <div>
                <NombreMarca fontSize={14} />
                <div style={{ fontSize: 10, color: 'var(--silver)', marginTop: 2 }}>Sistema de Gestión</div>
              </div>
            </div>
          </div>
        )}
        {collapsed && <LogoMarca size={32} />}
        <button
          onClick={onToggle}
          style={{
            background: 'rgba(var(--sky-rgb),0.15)', border: '1px solid rgba(var(--sky-rgb),0.3)',
            borderRadius: 6, padding: 4, cursor: 'pointer', color: 'var(--sky)',
            display: 'flex', alignItems: 'center', flexShrink: 0
          }}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
        {visibleItems.map(item => {
          const active = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              data-tour={item.key}
              onClick={() => handleNav(item.path)}
              title={collapsed ? item.label : undefined}
              style={{
                display: 'flex', alignItems: 'center',
                gap: collapsed ? 0 : 12,
                justifyContent: collapsed ? 'center' : 'flex-start',
                width: '100%', padding: collapsed ? '12px 0' : '11px 16px',
                marginBottom: 2,
                background: active
                  ? 'linear-gradient(90deg, rgba(var(--sky-rgb),0.25), rgba(var(--sky-rgb),0.05))'
                  : 'transparent',
                border: 'none',
                borderLeft: active ? '3px solid var(--sky)' : '3px solid transparent',
                cursor: 'pointer',
                color: active ? '#fff' : 'var(--silver)',
                fontSize: 14, fontFamily: 'var(--font)',
                fontWeight: active ? 600 : 400,
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap', overflow: 'hidden'
              }}
              onMouseEnter={e => {
                if (!active) e.currentTarget.style.background = 'rgba(var(--sky-rgb),0.08)';
              }}
              onMouseLeave={e => {
                if (!active) e.currentTarget.style.background = 'transparent';
              }}
            >
              <Icon size={18} style={{ flexShrink: 0, color: active ? 'var(--sky)' : 'var(--silver)' }} />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* User footer — compacto: solo usuario+rol; el botón despliega las opciones
          HACIA ARRIBA (menú flotante) para no tapar los módulos del menú. */}
      <div ref={footerRef} style={{
        padding: collapsed ? '12px 0' : '12px 12px',
        borderTop: '1px solid rgba(var(--silver-rgb),0.15)'
      }}>
        {user && (
          <button
            onClick={toggleUserMenu}
            title={collapsed ? user.nombre : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              justifyContent: collapsed ? 'center' : 'flex-start',
              width: '100%', padding: collapsed ? '8px 0' : '8px 10px',
              background: showUserMenu ? 'rgba(var(--sky-rgb),0.10)' : 'transparent',
              border: '1px solid ' + (showUserMenu ? 'rgba(var(--sky-rgb),0.25)' : 'transparent'),
              borderRadius: 10, cursor: 'pointer', color: 'var(--text)',
              fontFamily: 'var(--font)', transition: 'all 0.15s ease',
            }}
          >
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--sky), var(--steel))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, flexShrink: 0, color: '#fff'
            }}>
              {user.nombre?.charAt(0)}
            </div>
            {!collapsed && (
              <div style={{ overflow: 'hidden', flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user.nombre}
                </div>
                <div style={{ fontSize: 11, color: 'var(--silver)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {user.es_master ? <Shield size={10} style={{ color: 'var(--sky)' }} /> : null}
                  {user.rol_nombre}
                </div>
              </div>
            )}
            {!collapsed && <ChevronUp size={16} style={{ color: 'var(--silver)', flexShrink: 0, transform: showUserMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />}
          </button>
        )}
      </div>

      {/* Opciones del usuario: menú flotante que se abre hacia ARRIBA (fixed, no lo
          recorta el overflow del sidebar). Se cierra al elegir o al clicar fuera. */}
      {showUserMenu && menuRect && (
        <div ref={userMenuRef} style={{
          position: 'fixed', left: menuRect.left + 8, bottom: window.innerHeight - menuRect.top + 6,
          width: 216, zIndex: 1200, background: 'var(--tour-card)',
          border: '1px solid var(--border)', borderRadius: 12, padding: 6,
          boxShadow: '0 -8px 28px rgba(0,0,0,0.35)', display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          <button onClick={() => { setShowUserMenu(false); abrirOrden(); }} onMouseEnter={hoverIn} onMouseLeave={hoverOut} style={menuItemStyle}><ArrowUpDown size={15} /> Ordenar menú</button>
          <button onClick={() => { setShowUserMenu(false); abrirCambioPwd(); }} onMouseEnter={hoverIn} onMouseLeave={hoverOut} style={menuItemStyle}><KeyRound size={15} /> Cambiar contraseña</button>
          <button onClick={() => { setShowUserMenu(false); iniciarTour(); }} onMouseEnter={hoverIn} onMouseLeave={hoverOut} style={menuItemStyle}><HelpCircle size={15} /> Guía de inicio</button>
          <button onClick={handleLogout} onMouseEnter={hoverIn} onMouseLeave={hoverOut} style={{ ...menuItemStyle, color: 'var(--text-danger)' }}><LogOut size={15} /> Cerrar sesión</button>
        </div>
      )}

      {/* Modal: cambiar mi contraseña */}
      {showPwdModal && (
        <div className="modal-overlay" style={{ zIndex:2000 }} onClick={()=>{ if(!savingPwd) setShowPwdModal(false); }}>
          <div className="modal" style={{ maxWidth:420 }} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Cambiar contraseña</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>{ if(!savingPwd) setShowPwdModal(false); }}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">Contraseña actual</label>
                  <input className="form-input" type={showPwd?'text':'password'} value={curPwd} onChange={e=>setCurPwd(e.target.value)} />
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">Nueva contraseña</label>
                  <input className="form-input" type={showPwd?'text':'password'} value={newPwd} onChange={e=>setNewPwd(e.target.value)} />
                </div>
                <div className="form-group" style={{ margin:0 }}>
                  <label className="form-label">Confirmar nueva contraseña</label>
                  <input className="form-input" type={showPwd?'text':'password'} value={newPwd2} onChange={e=>setNewPwd2(e.target.value)} />
                </div>
                <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--silver)', cursor:'pointer' }}>
                  <input type="checkbox" checked={showPwd} onChange={e=>setShowPwd(e.target.checked)} style={{ accentColor:'var(--sky)' }} />
                  {showPwd ? <Eye size={13}/> : <EyeOff size={13}/>} Mostrar contraseñas
                </label>
                {pwdMsg && <div style={{ fontSize:13, color: pwdMsg.startsWith('✓') ? '#22c55e' : '#ef4444' }}>{pwdMsg}</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>{ if(!savingPwd) setShowPwdModal(false); }} disabled={savingPwd}>Cancelar</button>
              <button className="btn btn-primary" onClick={cambiarPwd} disabled={savingPwd}>{savingPwd?'Guardando...':'Cambiar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: ordenar mi menú */}
      {showOrden && (
        <div className="modal-overlay" style={{ zIndex:2000 }} onClick={()=>{ if(!savingOrden) setShowOrden(false); }}>
          <div className="modal" style={{ maxWidth:420 }} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Ordenar mi menú</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>{ if(!savingOrden) setShowOrden(false); }}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize:12, color:'var(--silver)', marginBottom:12 }}>
                Sube o baja cada módulo. El orden se guarda solo para tu usuario.
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {ordenList.map((it, idx) => (
                  <div key={it.key} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'rgba(var(--steel-rgb),0.4)', borderRadius:8 }}>
                    <span style={{ width:22, textAlign:'center', color:'var(--silver)', fontSize:12 }}>{idx+1}</span>
                    <span style={{ flex:1, fontSize:13, fontWeight:600 }}>{it.label}</span>
                    <button className="btn btn-icon btn-secondary btn-sm" disabled={idx===0} onClick={()=>moverModulo(idx,-1)} title="Subir"><ChevronUp size={14} /></button>
                    <button className="btn btn-icon btn-secondary btn-sm" disabled={idx===ordenList.length-1} onClick={()=>moverModulo(idx,1)} title="Bajar"><ChevronDown size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>{ if(!savingOrden) setShowOrden(false); }} disabled={savingOrden}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarOrden} disabled={savingOrden}>{savingOrden?'Guardando...':'Guardar orden'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

